import React, { useState } from 'react';
import { Calculator, Download, Users } from 'lucide-react';
import { personnel } from '../data/mock';
import { Button } from './ui/button';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Set up fonts for pdfMake
if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
}

const PriceCalculator = () => {
  const [guestCount, setGuestCount] = useState(1000);
  const [quantities, setQuantities] = useState({
    mainDish: 1,
    snack: 1,
    drink: 2,
    iceCream: 1,
    coffee: 1
  });
  
  const prices = {
    mainDish: 200,
    snack: 58,
    drink: 45,
    iceCream: 50,
    coffee: 60
  };
  
  // Calculate prices
  const calculateTotal = () => {
    let total = 0;
    
    // Food and beverages per person
    total += prices.mainDish * quantities.mainDish * guestCount;
    total += prices.snack * quantities.snack * guestCount;
    total += prices.drink * quantities.drink * guestCount;
    total += prices.iceCream * quantities.iceCream * guestCount;
    total += prices.coffee * quantities.coffee * guestCount;
    
    // Overhead costs (fixed for 1000 people, scale proportionally)
    const overheadPerPerson = 50000 / 1000;
    total += overheadPerPerson * guestCount;
    
    // Personnel costs (fixed for 1000 people, scale proportionally)
    const personnelPerPerson = 36700 / 1000;
    total += personnelPerPerson * guestCount;
    
    return total;
  };
  
  const updateQuantity = (item, delta) => {
    setQuantities(prev => ({
      ...prev,
      [item]: Math.max(0, prev[item] + delta)
    }));
  };
  
  const totalPrice = calculateTotal();
  const pricePerPerson = totalPrice / guestCount;
  
  const handleDownloadPDF = async () => {
    try {
      // Show loading state
      const button = document.querySelector('[data-download-pdf]');
      if (button) {
        button.textContent = 'Generuji PDF...';
        button.disabled = true;
      }
      
      // Create PDF document definition with pdfMake
      const docDefinition = {
        content: [
          // Header
          {
            text: 'Family Day 2026',
            style: 'header',
            alignment: 'center',
            color: '#00A0DC'
          },
          {
            text: 'Cateringová nabídka pro WANZL spol. s r.o.',
            style: 'subheader',
            alignment: 'center',
            margin: [0, 10, 0, 20]
          },
          
          // Event details
          {
            text: [
              { text: 'Datum: ', bold: true },
              '6. června 2026\n',
              { text: 'Lokalita: ', bold: true },
              'Hněvotín 333, 783 47 Hněvotín u Olomouce\n',
              { text: 'Počet osob: ', bold: true },
              `${guestCount}`
            ],
            margin: [0, 0, 0, 15]
          },
          
          // Line separator
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 515, y2: 0,
                lineWidth: 1,
                lineColor: '#00A0DC'
              }
            ],
            margin: [0, 0, 0, 15]
          },
          
          // Menu section
          {
            text: 'Nabídka pokrmů a nápojů na osobu:',
            style: 'sectionHeader',
            color: '#00A0DC'
          },
          {
            ul: [
              `Hlavní jídlo: ${quantities.mainDish}x (${prices.mainDish} Kč/ks)`,
              `Drobné občerstvení: ${quantities.snack}x (${prices.snack} Kč/ks)`,
              `Nápoj: ${quantities.drink}x (${prices.drink} Kč/ks)`,
              `Zmrzlina: ${quantities.iceCream}x (${prices.iceCream} Kč/ks)`,
              `Káva/teplý nápoj: ${quantities.coffee}x (${prices.coffee} Kč/ks)`
            ],
            margin: [0, 10, 0, 20]
          },
          
          // Price calculation
          {
            text: 'Kalkulace ceny:',
            style: 'sectionHeader',
            color: '#00A0DC'
          },
          {
            ul: [
              `Hlavní jídlo: ${(prices.mainDish * quantities.mainDish * guestCount).toLocaleString('cs-CZ')} Kč`,
              `Občerstvení: ${(prices.snack * quantities.snack * guestCount).toLocaleString('cs-CZ')} Kč`,
              `Nápoje: ${(prices.drink * quantities.drink * guestCount).toLocaleString('cs-CZ')} Kč`,
              `Zmrzlina: ${(prices.iceCream * quantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč`,
              `Káva: ${(prices.coffee * quantities.coffee * guestCount).toLocaleString('cs-CZ')} Kč`
            ],
            margin: [0, 10, 0, 10]
          },
          
          // Fixed costs
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 515, y2: 0,
                lineWidth: 0.5,
                lineColor: '#CCCCCC'
              }
            ],
            margin: [0, 5, 0, 10]
          },
          {
            text: [
              `Režie (vybavení, trucky, doprava): ${Math.round((50000 / 1000) * guestCount).toLocaleString('cs-CZ')} Kč\n`,
              `Personál (odhad ~10 hod): ${Math.round((36700 / 1000) * guestCount).toLocaleString('cs-CZ')} Kč`
            ],
            margin: [0, 0, 0, 20]
          },
          
          // Total price
          {
            table: {
              widths: ['*', 'auto'],
              body: [
                [
                  { text: 'CELKOVÁ CENA:', bold: true, fontSize: 16, color: 'white', fillColor: '#00A0DC' },
                  { text: `${Math.round(totalPrice).toLocaleString('cs-CZ')} Kč`, bold: true, fontSize: 16, color: 'white', fillColor: '#00A0DC', alignment: 'right' }
                ]
              ]
            },
            layout: 'noBorders',
            margin: [0, 0, 0, 15]
          },
          
          // Note
          {
            text: [
              'Všechny ceny jsou uvedeny BEZ DPH. ',
              'Finální cena bude upřesněna na základě skutečně odpracovaných hodin personálu.'
            ],
            style: 'note',
            alignment: 'center',
            margin: [0, 0, 0, 25]
          },
          
          // Contact info
          {
            text: 'Kontakt:',
            style: 'sectionHeader',
            color: '#00A0DC'
          },
          {
            text: [
              'Vše pro akci s.r.o.\n',
              'Jakub Vodárek, Jednatel\n',
              'Tel: 774 750 456\n',
              'Email: info@vseproakci.cz'
            ],
            margin: [0, 10, 0, 30]
          },
          
          // Footer
          {
            text: 'Nabídka platná do 1. 4. 2026',
            style: 'footer',
            alignment: 'center',
            color: '#999999'
          }
        ],
        styles: {
          header: {
            fontSize: 24,
            bold: true,
            margin: [0, 0, 0, 5]
          },
          subheader: {
            fontSize: 14,
            margin: [0, 0, 0, 10]
          },
          sectionHeader: {
            fontSize: 14,
            bold: true,
            margin: [0, 10, 0, 5]
          },
          note: {
            fontSize: 9,
            italics: true
          },
          footer: {
            fontSize: 8
          }
        },
        defaultStyle: {
          font: 'Roboto'
        }
      };
      
      // Generate and download PDF
      pdfMake.createPdf(docDefinition).download('Nabidka_Catering_Wanzl_Family_Day_2026.pdf');
      
      // Reset button state
      setTimeout(() => {
        if (button) {
          button.textContent = 'Stáhnout nabídku v PDF';
          button.disabled = false;
        }
      }, 500);
      
    } catch (error) {
      console.error('Chyba při generování PDF:', error);
      alert('Nepodařilo se vygenerovat PDF. Zkuste to prosím znovu.');
      
      // Reset button
      const button = document.querySelector('[data-download-pdf]');
      if (button) {
        button.textContent = 'Stáhnout nabídku v PDF';
        button.disabled = false;
      }
    }
  };
  
  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 bg-cyan-100 text-cyan-700 px-6 py-3 rounded-full mb-6">
              <Calculator className="w-6 h-6" />
              <span className="font-bold text-lg">Cenová kalkulačka</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Orientační cena dle počtu osob
            </h2>
            <p className="text-xl text-gray-600">
              Upravte počet hostů a zjistěte okamžitě orientační cenu
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-cyan-100">
            {/* Guest Counter */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-8 text-white">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <Users className="w-8 h-8" />
                <label className="text-2xl font-bold">Počet hostů</label>
              </div>
              <div className="flex items-center justify-center space-x-6">
                <button
                  onClick={() => setGuestCount(Math.max(1, guestCount - 50))}
                  className="bg-white/20 hover:bg-white/30 rounded-full w-14 h-14 flex items-center justify-center text-3xl font-bold transition-all duration-200 backdrop-blur-sm"
                >
                  −
                </button>
                <input
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-40 text-center text-5xl font-bold bg-white/20 backdrop-blur-sm rounded-2xl py-4 border-2 border-white/30 focus:outline-none focus:border-white"
                />
                <button
                  onClick={() => setGuestCount(guestCount + 50)}
                  className="bg-white/20 hover:bg-white/30 rounded-full w-14 h-14 flex items-center justify-center text-3xl font-bold transition-all duration-200 backdrop-blur-sm"
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Price Breakdown */}
            <div className="p-8">
              <div className="space-y-6 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Cenový rozpis</h3>
                
                {/* Per Person Items */}
                <div className="bg-cyan-50 rounded-xl p-6 border border-cyan-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Pokrmy a nápoje na osobu</h4>
                  <div className="space-y-4">
                    {/* Main Dish */}
                    <div className="bg-white rounded-lg p-4 border border-cyan-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Hlavní jídlo</span>
                        <span className="text-cyan-600 font-bold">{prices.mainDish} Kč/ks</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity('mainDish', -1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{quantities.mainDish}x</span>
                          <button
                            onClick={() => updateQuantity('mainDish', 1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(prices.mainDish * quantities.mainDish * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Snack */}
                    <div className="bg-white rounded-lg p-4 border border-cyan-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Drobné občerstvení</span>
                        <span className="text-cyan-600 font-bold">{prices.snack} Kč/ks</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity('snack', -1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{quantities.snack}x</span>
                          <button
                            onClick={() => updateQuantity('snack', 1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(prices.snack * quantities.snack * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Drink */}
                    <div className="bg-white rounded-lg p-4 border border-cyan-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Nápoj</span>
                        <span className="text-cyan-600 font-bold">{prices.drink} Kč/ks</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity('drink', -1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{quantities.drink}x</span>
                          <button
                            onClick={() => updateQuantity('drink', 1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(prices.drink * quantities.drink * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Ice Cream */}
                    <div className="bg-white rounded-lg p-4 border border-cyan-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Zmrzlina</span>
                        <span className="text-cyan-600 font-bold">{prices.iceCream} Kč/ks</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity('iceCream', -1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{quantities.iceCream}x</span>
                          <button
                            onClick={() => updateQuantity('iceCream', 1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(prices.iceCream * quantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Coffee */}
                    <div className="bg-white rounded-lg p-4 border border-cyan-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Káva / teplý nápoj</span>
                        <span className="text-cyan-600 font-bold">{prices.coffee} Kč/ks</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity('coffee', -1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{quantities.coffee}x</span>
                          <button
                            onClick={() => updateQuantity('coffee', 1)}
                            className="bg-cyan-100 hover:bg-cyan-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(prices.coffee * quantities.coffee * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Fixed Costs */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Fixní náklady</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Režie (vybavení, trucky, doprava)</span>
                      <span className="font-semibold text-gray-900">
                        {Math.round((50000 / 1000) * guestCount).toLocaleString('cs-CZ')} Kč
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 italic">
                      Zahrnuje: obaly, cateringové vybavení, 2x Food/Beer truck, Bar truck, 2x Live cooking + kompletní doprava
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-gray-300">
                      <span className="text-gray-700">Personál (odhad ~10 hod)</span>
                      <span className="font-semibold text-gray-900">
                        {Math.round((36700 / 1000) * guestCount).toLocaleString('cs-CZ')} Kč
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 italic">
                      3x Kuchař, 3x Pomocný kuchař, 4x Obsluha, 5x Barman. Účtováno dle skutečně odpracovaných hodin.
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Total Price */}
              <div className="border-t-2 border-gray-200 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xl font-bold text-gray-900">Cena na osobu:</span>
                  <span className="text-3xl font-bold text-cyan-600">
                    {Math.round(pricePerPerson).toLocaleString('cs-CZ')} Kč
                  </span>
                </div>
                <div className="flex justify-between items-center bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-6 text-white">
                  <span className="text-2xl font-bold">Celková cena:</span>
                  <span className="text-4xl font-bold">
                    {Math.round(totalPrice).toLocaleString('cs-CZ')} Kč
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mt-6 italic text-center">
                Všechny ceny jsou uvedeny BEZ DPH. Finální cena bude upřesněna na základě skutečně odpracovaných hodin personálu.
              </p>
              
              {/* Download Button */}
              <div className="mt-8 text-center">
                <Button
                  onClick={handleDownloadPDF}
                  data-download-pdf
                  size="lg"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Stáhnout nabídku v PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriceCalculator;
